import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeStructuralGenerationTier,
} from '@/lib/treeGenerationAuthority';

import {
  computeTreeBranchPaths,
} from '@/lib/treeBranchPathAuthority';

export interface CanonicalTreeTopology {
  generationByMemberId: Record<string, number>;
  branchPathByMemberId: Record<string, string>;
}

export function computeCanonicalTreeTopology(
  members: GraphMember[],
  viewerMemberId: string | null,
): CanonicalTreeTopology {
  const generationByMemberId:
    Record<string, number> = {};

  const branchPathByMemberId:
    Record<string, string> = {};

  if (
    !viewerMemberId ||
    !members.some(
      member => member.id === viewerMemberId,
    )
  ) {
    return {
      generationByMemberId,
      branchPathByMemberId,
    };
  }

  for (const member of members) {
    const tier =
      computeStructuralGenerationTier(
        members,
        viewerMemberId,
        member.id,
      );

    if (tier !== null) {
      generationByMemberId[member.id] =
        tier;
    }
  }

  const paths = computeTreeBranchPaths(
    members,
    viewerMemberId,
  );

  for (const [memberId, path] of paths) {
    branchPathByMemberId[memberId] =
      path;
  }

  return {
    generationByMemberId,
    branchPathByMemberId,
  };
}

/**
 * CC-TREE-LIVE-003G3A
 *
 * EXTENDED_GENERATION_ANCHOR_AUTHORITY
 *
 * Display titles never grant ancestry placement authority.
 *
 * A grandparent / great-grandparent writer anchor must be:
 *
 * 1. a live structural ancestor of the viewer;
 * 2. on the exact generation required by the requested relationship;
 * 3. on the requested maternal/paternal side when that side is explicit;
 * 4. distinct from the existing subject when reconnecting a profile.
 *
 * The core `great-grandparent` relationship intentionally represents
 * multiple display depths. Therefore the virtual option key is required
 * to distinguish Great-Great-Grandparent from ordinary Great-Grandparent.
 */
export type CanonicalTreeGenerationAnchorStatus =
  | 'NOT_APPLICABLE'
  | 'AUTHORIZED_GENERATION_ANCHOR'
  | 'AUTHORIZED_FLOATING_GENERATION_PROFILE'
  | 'REJECTED_MISSING_VIEWER'
  | 'REJECTED_MISSING_ANCHOR'
  | 'REJECTED_SUBJECT_AS_ANCHOR'
  | 'REJECTED_CROSS_GRAPH'
  | 'REJECTED_PRESENTATION_DEPTH_MISMATCH'
  | 'REJECTED_UNSUPPORTED_OPTION'
  | 'REJECTED_NOT_STRUCTURAL_ANCESTOR'
  | 'REJECTED_INCOMPLETE_ANCESTRY_EVIDENCE'
  | 'REJECTED_UNRESOLVED_ANCHOR_GENERATION'
  | 'REJECTED_WRONG_ANCHOR_GENERATION'
  | 'REJECTED_UNRESOLVED_BRANCH'
  | 'REJECTED_BRANCH_EVIDENCE_CONFLICT'
  | 'REJECTED_BRANCH_MISMATCH';

export interface CanonicalTreeGenerationAnchorDecision {
  readonly applies:
    boolean;

  readonly authorized:
    boolean;

  readonly status:
    CanonicalTreeGenerationAnchorStatus;

  readonly requiredAnchorTier:
    number | null;

  readonly actualAnchorTier:
    number | null;

  readonly anchorMemberId:
    string | null;

  readonly reason:
    string;
}

export interface CanonicalTreeGenerationAnchorInput {
  readonly members:
    GraphMember[];

  readonly viewerMemberId:
    string | null;

  readonly relationship:
    GraphMember['relationship'];

  readonly optionKey?:
    string | null;

  /**
   * Optional presentation title.
   *
   * Custom titles remain legal, but a title that itself encodes a direct
   * ancestor depth may not contradict the canonical option depth.
   */
  readonly relationshipOverride?:
    string | null;

  readonly preferredFamilyTitle?:
    string | null;

  /**
   * Profile creation may intentionally remain floating when the descendant
   * anchor is not yet known. This grants zero ancestry placement authority.
   */
  readonly allowMissingAnchor?:
    boolean;

  readonly anchorMemberId?:
    string | null;

  readonly subjectMemberId?:
    string | null;

  readonly side?:
    GraphMember['side'] | null;
}

const requiredGenerationAnchorTier = (
  relationship:
    GraphMember['relationship'],
  optionKey:
    string | null | undefined,
): {
  applies: boolean;
  requiredTier: number | null;
  supported: boolean;
  mode:
    'FIXED' |
    'UNBOUNDED';
  minimumTier:
    number | null;
} => {
  if (
    relationship ===
    'grandparent'
  ) {
    return {
      applies: true,
      requiredTier: 1,
      supported:
        optionKey === null ||
        optionKey === undefined ||
        optionKey ===
          'grandparent',
      mode:
        'FIXED',
      minimumTier:
        null,
    };
  }

  if (
    relationship ===
    'great-grandparent'
  ) {
    /*
     * KinsCircle direct ancestry is not capped by the old
     * Famous-era relationship vocabulary.
     *
     * Earlier Ancestor means:
     *   "parent of the selected structurally-proven ancestor"
     *
     * The anchor's real topology determines the generation.
     */
    if (
      optionKey ===
      'v_earlier_ancestor'
    ) {
      return {
        applies: true,
        requiredTier: null,
        supported: true,
        mode:
          'UNBOUNDED',
        minimumTier:
          4,
      };
    }

    /*
     * Preserve the certified 003G3A key explicitly.
     */
    if (
      optionKey ===
      'v_great_great_grandparent'
    ) {
      return {
        applies: true,
        requiredTier: 3,
        supported: true,
        mode:
          'FIXED',
        minimumTier:
          null,
      };
    }

    /*
     * Forward-compatible arbitrary-depth keys:
     *
     * v_great_great_great_grandparent
     * v_great_great_great_great_grandparent
     * ...
     *
     * The number of "great_" segments determines presentation
     * depth only because the option key itself is the governed
     * structural intent — free-form labels still grant zero
     * ancestry authority.
     */
    if (
      optionKey &&
      /^v_(?:great_){3,}grandparent$/.test(
        optionKey,
      )
    ) {
      const greatLevels =
        (
          optionKey.match(
            /great_/g,
          ) ?? []
        ).length;

      return {
        applies: true,
        requiredTier:
          greatLevels + 1,
        supported: true,
        mode:
          'FIXED',
        minimumTier:
          null,
      };
    }

    if (
      optionKey === null ||
      optionKey === undefined ||
      optionKey ===
        'great-grandparent'
    ) {
      return {
        applies: true,
        requiredTier: 2,
        supported: true,
        mode:
          'FIXED',
        minimumTier:
          null,
      };
    }

    return {
      applies: true,
      requiredTier: null,
      supported: false,
      mode:
        'FIXED',
      minimumTier:
        null,
    };
  }

  return {
    applies: false,
    requiredTier: null,
    supported: true,
    mode:
      'FIXED',
    minimumTier:
      null,
  };
};

export const directAncestorDepthFromTitle = (
  value:
    string | null | undefined,
): number | null => {
  if (!value) {
    return null;
  }

  const normalized =
    value
      .trim()
      .toLowerCase()
      .replace(
        /[_\s]+/g,
        '-',
      )
      .replace(
        /-+/g,
        '-',
      );

  const match =
    normalized.match(
      /^((?:great-)*)(grandparent|grandmother|grandfather)$/,
    );

  if (!match) {
    return null;
  }

  const greatLevels =
    (
      match[1].match(
        /great-/g,
      ) ?? []
    ).length;

  return 2 + greatLevels;
};

const isActiveGenerationAnchorMember = (
  member:
    GraphMember,
): boolean =>
  member.approvalStatus !==
    'rejected' &&
  String(
    member.profileState ??
      '',
  ) !==
    'deleted';

const activeGenerationGraphMembers = (
  members:
    GraphMember[],
  viewer:
    GraphMember,
): GraphMember[] =>
  members.filter(
    member =>
      isActiveGenerationAnchorMember(
        member,
      ) &&
      member.ownerUserId ===
        viewer.ownerUserId,
  );

export type CanonicalTreeStructuralAncestorBranch =
  | 'maternal'
  | 'paternal'
  | 'both'
  | 'unresolved'
  | 'conflict';

export type CanonicalTreeDirectAncestorEvidenceStatus =
  | 'PROVEN'
  | 'NOT_PROVEN'
  | 'INDETERMINATE';

export interface CanonicalTreeDirectAncestorEvidence {
  readonly status:
    CanonicalTreeDirectAncestorEvidenceStatus;

  readonly exactDepth:
    number | null;

  readonly branch:
    CanonicalTreeStructuralAncestorBranch;

  readonly missingReferencedMemberIds:
    readonly string[];
}

interface CanonicalTreeDirectAncestorWalk {
  readonly depthByMemberId:
    ReadonlyMap<string, number>;

  readonly missingReferencedMemberIds:
    ReadonlySet<string>;

  readonly minimumMissingDepth:
    number | null;
}

interface CanonicalTreeDirectAncestorFirstHop {
  readonly branch:
    CanonicalTreeStructuralAncestorBranch;

  readonly walk:
    CanonicalTreeDirectAncestorWalk | null;

  readonly indeterminate:
    boolean;
}

interface CanonicalTreeDirectAncestorContext {
  readonly viewer:
    GraphMember | null;

  readonly authoritativeById:
    ReadonlyMap<string, GraphMember>;

  readonly globalWalk:
    CanonicalTreeDirectAncestorWalk;

  readonly firstHops:
    readonly CanonicalTreeDirectAncestorFirstHop[];
}

const directViewerParentBranchEvidence = (
  parent:
    GraphMember,
): CanonicalTreeStructuralAncestorBranch => {
  const relationshipBranch =
    parent.relationship ===
      'mother'
      ? 'maternal'
      : parent.relationship ===
          'father'
        ? 'paternal'
        : null;

  const sideBranch =
    parent.side ===
      'mother'
      ? 'maternal'
      : parent.side ===
          'father'
        ? 'paternal'
        : parent.side ===
            'both'
          ? 'both'
          : null;

  if (
    relationshipBranch &&
    sideBranch &&
    sideBranch !==
      'both' &&
    relationshipBranch !==
      sideBranch
  ) {
    return 'conflict';
  }

  if (relationshipBranch) {
    return relationshipBranch;
  }

  if (sideBranch) {
    return sideBranch;
  }

  return 'unresolved';
};

const emptyDirectAncestorWalk =
  (): CanonicalTreeDirectAncestorWalk => ({
    depthByMemberId:
      new Map<string, number>(),

    missingReferencedMemberIds:
      new Set<string>(),

    minimumMissingDepth:
      null,
  });

const walkCanonicalTreeDirectAncestry = (
  seedId:
    string,

  allById:
    ReadonlyMap<string, GraphMember>,

  authoritativeById:
    ReadonlyMap<string, GraphMember>,
): CanonicalTreeDirectAncestorWalk => {
  if (
    !authoritativeById.has(seedId)
  ) {
    return emptyDirectAncestorWalk();
  }

  const depthByMemberId =
    new Map<string, number>([
      [seedId, 0],
    ]);

  const missingReferencedMemberIds =
    new Set<string>();

  let minimumMissingDepth:
    number | null =
    null;

  const queue:
    Array<{
      id: string;
      depth: number;
    }> = [
      {
        id:
          seedId,
        depth:
          0,
      },
    ];

  while (queue.length > 0) {
    const current =
      queue.shift();

    if (!current) {
      continue;
    }

    const member =
      authoritativeById.get(
        current.id,
      );

    if (!member) {
      continue;
    }

    for (
      const parentId of
      member.parentIds ??
      []
    ) {
      if (!parentId) {
        continue;
      }

      const nextDepth =
        current.depth + 1;

      /*
       * Absent from all supplied evidence:
       * indeterminate.
       *
       * Supplied but inactive/foreign:
       * known non-authoritative boundary.
       */
      if (!allById.has(parentId)) {
        missingReferencedMemberIds.add(
          parentId,
        );

        minimumMissingDepth =
          minimumMissingDepth === null
            ? nextDepth
            : Math.min(
                minimumMissingDepth,
                nextDepth,
              );

        continue;
      }

      if (
        !authoritativeById.has(
          parentId,
        )
      ) {
        continue;
      }

      /*
       * FIFO traversal means first discovery is
       * shortest in this unit-edge ancestry graph.
       * It also makes malformed cycles terminate.
       */
      if (
        depthByMemberId.has(
          parentId,
        )
      ) {
        continue;
      }

      depthByMemberId.set(
        parentId,
        nextDepth,
      );

      queue.push({
        id:
          parentId,
        depth:
          nextDepth,
      });
    }
  }

  return {
    depthByMemberId,
    missingReferencedMemberIds,
    minimumMissingDepth,
  };
};

const buildCanonicalTreeDirectAncestorContext = (
  members:
    GraphMember[],

  viewerMemberId:
    string,
): CanonicalTreeDirectAncestorContext => {
  const allById =
    new Map(
      members.map(
        member =>
          [
            member.id,
            member,
          ] as const,
      ),
    );

  const viewer =
    allById.get(
      viewerMemberId,
    ) ??
    null;

  if (
    !viewer ||
    !isActiveGenerationAnchorMember(
      viewer,
    )
  ) {
    return {
      viewer:
        null,
      authoritativeById:
        new Map(),
      globalWalk:
        emptyDirectAncestorWalk(),
      firstHops:
        [],
    };
  }

  const authoritativeById =
    new Map(
      members
        .filter(
          member =>
            isActiveGenerationAnchorMember(
              member,
            ) &&
            member.ownerUserId ===
              viewer.ownerUserId,
        )
        .map(
          member =>
            [
              member.id,
              member,
            ] as const,
        ),
    );

  const globalWalk =
    walkCanonicalTreeDirectAncestry(
      viewer.id,
      allById,
      authoritativeById,
    );

  const firstHops:
    CanonicalTreeDirectAncestorFirstHop[] =
      [];

  for (
    const parentId of
    Array.from(
      new Set(
        (
          viewer.parentIds ??
          []
        ).filter(Boolean),
      ),
    )
  ) {
    const supplied =
      allById.get(
        parentId,
      );

    if (!supplied) {
      firstHops.push({
        branch:
          'unresolved',
        walk:
          null,
        indeterminate:
          true,
      });

      continue;
    }

    const parent =
      authoritativeById.get(
        parentId,
      );

    if (!parent) {
      continue;
    }

    const walk =
      walkCanonicalTreeDirectAncestry(
        parent.id,
        allById,
        authoritativeById,
      );

    firstHops.push({
      branch:
        directViewerParentBranchEvidence(
          parent,
        ),
      walk,
      indeterminate:
        walk
          .missingReferencedMemberIds
          .size > 0,
    });
  }

  return {
    viewer,
    authoritativeById,
    globalWalk,
    firstHops,
  };
};

const combineProvenAncestorBranches = (
  values:
    readonly CanonicalTreeStructuralAncestorBranch[],
): CanonicalTreeStructuralAncestorBranch => {
  if (
    values.includes(
      'conflict',
    )
  ) {
    return 'conflict';
  }

  if (
    values.includes(
      'unresolved',
    )
  ) {
    return 'unresolved';
  }

  if (
    values.includes(
      'both',
    )
  ) {
    return 'both';
  }

  const unique =
    new Set(values);

  if (
    unique.has('maternal') &&
    unique.has('paternal')
  ) {
    return 'both';
  }

  if (unique.has('maternal')) {
    return 'maternal';
  }

  if (unique.has('paternal')) {
    return 'paternal';
  }

  return 'unresolved';
};

const resolveCanonicalTreeDirectAncestorEvidenceFromContext = (
  context:
    CanonicalTreeDirectAncestorContext,

  ancestorMemberId:
    string,
): CanonicalTreeDirectAncestorEvidence => {
  const missing =
    Array.from(
      context
        .globalWalk
        .missingReferencedMemberIds,
    ).sort();

  if (
    !context.viewer ||
    ancestorMemberId ===
      context.viewer.id ||
    !context
      .authoritativeById
      .has(
        ancestorMemberId,
      )
  ) {
    return {
      status:
        'NOT_PROVEN',
      exactDepth:
        null,
      branch:
        'unresolved',
      missingReferencedMemberIds:
        missing,
    };
  }

  const provenDepth =
    context
      .globalWalk
      .depthByMemberId
      .get(
        ancestorMemberId,
      );

  if (
    provenDepth === undefined
  ) {
    return {
      status:
        missing.length > 0
          ? 'INDETERMINATE'
          : 'NOT_PROVEN',
      exactDepth:
        null,
      branch:
        'unresolved',
      missingReferencedMemberIds:
        missing,
    };
  }

  /*
   * Missing frontier F can create a shorter
   * path only if F + 1 < provenDepth.
   */
  const depthIndeterminate =
    context
      .globalWalk
      .minimumMissingDepth !==
      null &&
    context
      .globalWalk
      .minimumMissingDepth +
      1 <
      provenDepth;

  const reachableBranches:
    CanonicalTreeStructuralAncestorBranch[] =
      [];

  const potentialBranches:
    CanonicalTreeStructuralAncestorBranch[] =
      [];

  for (
    const firstHop of
    context.firstHops
  ) {
    if (
      firstHop.walk
        ?.depthByMemberId
        .has(
          ancestorMemberId,
        )
    ) {
      reachableBranches.push(
        firstHop.branch,
      );
      continue;
    }

    if (
      firstHop.indeterminate
    ) {
      potentialBranches.push(
        firstHop.branch,
      );
    }
  }

  let branch =
    combineProvenAncestorBranches(
      reachableBranches,
    );

  if (
    branch !== 'both' &&
    branch !== 'conflict' &&
    branch !== 'unresolved'
  ) {
    const couldChangeBranch =
      potentialBranches.some(
        potential =>
          potential ===
            'unresolved' ||
          potential ===
            'conflict' ||
          potential ===
            'both' ||
          potential !==
            branch,
      );

    if (couldChangeBranch) {
      branch =
        'unresolved';
    }
  }

  return {
    status:
      'PROVEN',
    exactDepth:
      depthIndeterminate
        ? null
        : provenDepth,
    branch,
    missingReferencedMemberIds:
      missing,
  };
};

export const resolveCanonicalTreeDirectAncestorEvidence = (
  input: {
    readonly members:
      GraphMember[];

    readonly viewerMemberId:
      string;

    readonly ancestorMemberId:
      string;
  },
): CanonicalTreeDirectAncestorEvidence => {
  const context =
    buildCanonicalTreeDirectAncestorContext(
      input.members,
      input.viewerMemberId,
    );

  return resolveCanonicalTreeDirectAncestorEvidenceFromContext(
    context,
    input.ancestorMemberId,
  );
};

export const resolveCanonicalTreeStructuralAncestorBranch = (
  input: {
    readonly members:
      GraphMember[];

    readonly viewerMemberId:
      string;

    readonly ancestorMemberId:
      string;
  },
): CanonicalTreeStructuralAncestorBranch => {
  const evidence =
    resolveCanonicalTreeDirectAncestorEvidence(
      input,
    );

  return evidence.status ===
    'PROVEN'
    ? evidence.branch
    : 'unresolved';
};


export function resolveCanonicalTreeGenerationAnchorAuthority(
  input:
    CanonicalTreeGenerationAnchorInput,
): CanonicalTreeGenerationAnchorDecision {
  const requirement =
    requiredGenerationAnchorTier(
      input.relationship,
      input.optionKey,
    );

  if (
    !requirement.applies
  ) {
    return {
      applies: false,
      authorized: true,
      status:
        'NOT_APPLICABLE',
      requiredAnchorTier:
        null,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId ??
        null,
      reason:
        'Relationship does not use generation-anchor authority.',
    };
  }

  if (
    !requirement.supported
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_UNSUPPORTED_OPTION',
      requiredAnchorTier:
        null,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId ??
        null,
      reason:
        'Extended ancestor intent is not a supported canonical generation option.',
    };
  }

  /*
   * Presentation depth can describe the family member, but it cannot
   * contradict the structural option being executed.
   */
  const expectedAncestorDepth =
    requirement.requiredTier ===
      null
      ? null
      : requirement.requiredTier +
        1;

  if (
    expectedAncestorDepth !==
    null
  ) {
    for (
      const title of [
        input.relationshipOverride,
        input.preferredFamilyTitle,
      ]
    ) {
      const presentationDepth =
        directAncestorDepthFromTitle(
          title,
        );

      if (
        presentationDepth !==
          null &&
        presentationDepth !==
          expectedAncestorDepth
      ) {
        return {
          applies: true,
          authorized: false,
          status:
            'REJECTED_PRESENTATION_DEPTH_MISMATCH',
          requiredAnchorTier:
            requirement.requiredTier,
          actualAnchorTier:
            null,
          anchorMemberId:
            input.anchorMemberId ??
            null,
          reason:
            `Ancestor presentation depth ${presentationDepth} contradicts canonical structural depth ${expectedAncestorDepth}.`,
        };
      }
    }
  }

  if (
    !input.viewerMemberId
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_MISSING_VIEWER',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId ??
        null,
      reason:
        'Viewer identity is required before ancestor generation can be authorized.',
    };
  }

  const liveViewer =
    input.members.find(
      member =>
        member.id ===
          input.viewerMemberId &&
        isActiveGenerationAnchorMember(
          member,
        ),
    ) ??
    null;

  if (!liveViewer) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_MISSING_VIEWER',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId ??
        null,
      reason:
        'Viewer identity is not present as an active member of the loaded graph.',
    };
  }

  if (
    !input.anchorMemberId &&
    requirement.mode ===
      'UNBOUNDED'
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_MISSING_ANCHOR',
      requiredAnchorTier:
        null,
      actualAnchorTier:
        null,
      anchorMemberId:
        null,
      reason:
        'An unbounded ancestor requires a proven structural descendant anchor before generation can be established.',
    };
  }

  /*
   * Compatibility authority:
   *
   * Add Member may persist an ancestor profile before its descendant
   * connector is known. That is profile creation only, not ancestry
   * authorization. No parent edge may be emitted from this decision.
   */
  if (
    !input.anchorMemberId &&
    input.allowMissingAnchor ===
      true
  ) {
    return {
      applies: true,
      authorized: true,
      status:
        'AUTHORIZED_FLOATING_GENERATION_PROFILE',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        null,
      reason:
        'Ancestor profile may remain floating until a canonical descendant anchor is explicitly connected.',
    };
  }

  if (
    !input.anchorMemberId
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_MISSING_ANCHOR',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        null,
      reason:
        'A structural descendant anchor is required for this ancestor relationship.',
    };
  }

  if (
    input.subjectMemberId &&
    input.subjectMemberId ===
      input.anchorMemberId
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_SUBJECT_AS_ANCHOR',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId,
      reason:
        'A member cannot be its own descendant anchor.',
    };
  }

  const liveAnchor =
    input.members.find(
      member =>
        member.id ===
          input.anchorMemberId &&
        isActiveGenerationAnchorMember(
          member,
        ),
    ) ??
    null;

  if (!liveAnchor) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_MISSING_ANCHOR',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        input.anchorMemberId,
      reason:
        'The requested ancestor anchor is not a live member of the loaded graph.',
    };
  }

  if (
    liveAnchor.ownerUserId !==
    liveViewer.ownerUserId
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_CROSS_GRAPH',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        liveAnchor.id,
      reason:
        'Viewer and ancestor anchor must belong to the same canonical graph.',
    };
  }

  const ancestorEvidence =
    resolveCanonicalTreeDirectAncestorEvidence({
      members:
        input.members,
      viewerMemberId:
        liveViewer.id,
      ancestorMemberId:
        liveAnchor.id,
    });

  if (
    ancestorEvidence.status ===
      'INDETERMINATE'
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_INCOMPLETE_ANCESTRY_EVIDENCE',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        liveAnchor.id,
      reason:
        'Direct-ancestor evidence is incomplete; missing structural references prevent ancestry authorization.',
    };
  }

  if (
    ancestorEvidence.status !==
      'PROVEN'
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_NOT_STRUCTURAL_ANCESTOR',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        liveAnchor.id,
      reason:
        'The requested anchor is not a structurally proven ancestor of the viewer.',
    };
  }

  if (
    ancestorEvidence.exactDepth ===
      null
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_INCOMPLETE_ANCESTRY_EVIDENCE',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        null,
      anchorMemberId:
        liveAnchor.id,
      reason:
        'A direct-ancestor path is proven, but incomplete structural evidence prevents an exact generation tier.',
    };
  }

  const actualTier =
    ancestorEvidence.exactDepth;

  const effectiveRequiredTier =
    requirement.mode ===
      'UNBOUNDED'
      ? actualTier
      : requirement.requiredTier;

  if (
    requirement.mode ===
      'UNBOUNDED'
  ) {
    if (
      actualTier <
      (
        requirement.minimumTier ??
        4
      )
    ) {
      return {
        applies: true,
        authorized: false,
        status:
          'REJECTED_WRONG_ANCHOR_GENERATION',
        requiredAnchorTier:
          requirement.minimumTier,
        actualAnchorTier:
          actualTier,
        anchorMemberId:
          liveAnchor.id,
        reason:
          'Earlier-ancestor intent requires a structural anchor at tier ' +
          String(requirement.minimumTier ?? 4) +
          ' or deeper; received tier ' +
          String(actualTier) +
          '.',
      };
    }

    for (
      const title of [
        input.relationshipOverride,
        input.preferredFamilyTitle,
      ]
    ) {
      const presentationDepth =
        directAncestorDepthFromTitle(
          title,
        );

      if (
        presentationDepth !==
          null &&
        presentationDepth !==
          actualTier + 1
      ) {
        return {
          applies: true,
          authorized: false,
          status:
            'REJECTED_PRESENTATION_DEPTH_MISMATCH',
          requiredAnchorTier:
            actualTier,
          actualAnchorTier:
            actualTier,
          anchorMemberId:
            liveAnchor.id,
          reason:
            'Ancestor presentation depth ' +
            String(presentationDepth) +
            ' contradicts canonical structural depth ' +
            String(actualTier + 1) +
            '.',
        };
      }
    }
  } else if (
    actualTier !==
    requirement.requiredTier
  ) {
    return {
      applies: true,
      authorized: false,
      status:
        'REJECTED_WRONG_ANCHOR_GENERATION',
      requiredAnchorTier:
        requirement.requiredTier,
      actualAnchorTier:
        actualTier,
      anchorMemberId:
        liveAnchor.id,
      reason:
        'Ancestor anchor generation mismatch: required tier ' +
        String(requirement.requiredTier) +
        ', received tier ' +
        String(actualTier) +
        '.',
    };
  }

  if (
    input.side ===
      'mother' ||
    input.side ===
      'father'
  ) {
    const structuralBranch =
      ancestorEvidence.branch;

    if (
      structuralBranch ===
        'conflict'
    ) {
      return {
        applies: true,
        authorized: false,
        status:
          'REJECTED_BRANCH_EVIDENCE_CONFLICT',
        requiredAnchorTier:
          effectiveRequiredTier,
        actualAnchorTier:
          actualTier,
        anchorMemberId:
          liveAnchor.id,
        reason:
          'Direct viewer-parent relationship and side evidence conflict; ancestor branch authority is indeterminate.',
      };
    }

    if (
      structuralBranch ===
        'unresolved'
    ) {
      return {
        applies: true,
        authorized: false,
        status:
          'REJECTED_UNRESOLVED_BRANCH',
        requiredAnchorTier:
          effectiveRequiredTier,
        actualAnchorTier:
          actualTier,
        anchorMemberId:
          liveAnchor.id,
        reason:
          'Ancestor branch cannot be proven from complete viewer-relative first-hop evidence.',
      };
    }

    const requestedBranch =
      input.side ===
        'mother'
        ? 'maternal'
        : 'paternal';

    if (
      structuralBranch !==
        requestedBranch &&
      structuralBranch !==
        'both'
    ) {
      return {
        applies: true,
        authorized: false,
        status:
          'REJECTED_BRANCH_MISMATCH',
        requiredAnchorTier:
          effectiveRequiredTier,
        actualAnchorTier:
          actualTier,
        anchorMemberId:
          liveAnchor.id,
        reason:
          'Ancestor structural branch does not match the requested family side.',
      };
    }
  }

  return {
    applies: true,
    authorized: true,
    status:
      'AUTHORIZED_GENERATION_ANCHOR',
    requiredAnchorTier:
      effectiveRequiredTier,
    actualAnchorTier:
      actualTier,
    anchorMemberId:
      liveAnchor.id,
    reason:
      'Structural ancestor, generation tier, and requested branch are authorized.',
  };
}

export function selectCanonicalTreeGenerationAnchorCandidates(
  input:
    Omit<
      CanonicalTreeGenerationAnchorInput,
      'anchorMemberId'
    >,
): GraphMember[] {
  const requirement =
    requiredGenerationAnchorTier(
      input.relationship,
      input.optionKey,
    );

  if (
    !requirement.applies ||
    !requirement.supported ||
    !input.viewerMemberId
  ) {
    return [];
  }

  const viewer =
    input.members.find(
      member =>
        member.id ===
          input.viewerMemberId &&
        isActiveGenerationAnchorMember(
          member,
        ),
    ) ??
    null;

  if (!viewer) {
    return [];
  }

  const graphMembers =
    activeGenerationGraphMembers(
      input.members,
      viewer,
    );

  const directAncestryContext =
    buildCanonicalTreeDirectAncestorContext(
      input.members,
      viewer.id,
    );

  return graphMembers.filter(
    candidate => {
      if (
        !isActiveGenerationAnchorMember(
          candidate,
        )
      ) {
        return false;
      }

      if (
        input.subjectMemberId &&
        candidate.id ===
          input.subjectMemberId
      ) {
        return false;
      }

      if (
        candidate.ownerUserId !==
        viewer.ownerUserId
      ) {
        return false;
      }

      const ancestorEvidence =
        resolveCanonicalTreeDirectAncestorEvidenceFromContext(
          directAncestryContext,
          candidate.id,
        );

      if (
        ancestorEvidence.status !==
          'PROVEN' ||
        ancestorEvidence.exactDepth ===
          null
      ) {
        return false;
      }

      const candidateTier =
        ancestorEvidence.exactDepth;

      if (
        requirement.mode ===
          'UNBOUNDED'
      ) {
        if (
          candidateTier <
          (
            requirement.minimumTier ??
            4
          )
        ) {
          return false;
        }
      } else if (
        candidateTier !==
        requirement.requiredTier
      ) {
        return false;
      }

      if (
        input.side ===
          'mother' ||
        input.side ===
          'father'
      ) {
        const structuralBranch =
          ancestorEvidence.branch;

        const requestedBranch =
          input.side ===
            'mother'
            ? 'maternal'
            : 'paternal';

        if (
          structuralBranch !==
            requestedBranch &&
          structuralBranch !==
            'both'
        ) {
          return false;
        }
      }

      return true;
    },
  );
}
